import React, { useState } from "react";
import { ShieldCheck, Eye, EyeOff, KeyRound } from "lucide-react";
import axios from "axios";
import { TextInput, showAlert, showErrorAlert } from "../../../../../../../components/ui/index.js";

const SeguridadUserProfile = () => {
    const [formData, setFormData] = useState({
        old_password: "",
        new_password: "",
        confirm_password: "",
    });

    const [showPasswords, setShowPasswords] = useState({
        old_password: false,
        new_password: false,
        confirm_password: false,
    });

    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevState) => ({
            ...prevState,
            [name]: value,
        }));

        // Limpiar errores cuando el usuario empiece a escribir
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords((prev) => ({
            ...prev,
            [field]: !prev[field],
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.old_password.trim()) {
            newErrors.old_password = "La contraseña actual es requerida";
        }

        if (!formData.new_password.trim()) {
            newErrors.new_password = "La nueva contraseña es requerida";
        } else if (formData.new_password.length < 8) {
            newErrors.new_password = "La contraseña debe tener al menos 8 caracteres";
        } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.new_password)) {
            newErrors.new_password = "La contraseña debe contener al menos una mayúscula, una minúscula y un número";
        }

        if (!formData.confirm_password.trim()) {
            newErrors.confirm_password = "Confirma tu nueva contraseña";
        } else if (formData.new_password !== formData.confirm_password) {
            newErrors.confirm_password = "Las contraseñas no coinciden";
        }

        if (formData.old_password === formData.new_password && formData.old_password.trim()) {
            newErrors.new_password = "La nueva contraseña debe ser diferente a la actual";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsLoading(true);

        try {
            await axios.put("/change_password/", {
                old_password: formData.old_password,
                new_password: formData.new_password,
                confirm_password: formData.confirm_password,
            });

            showAlert({
                title: "Contraseña actualizada",
                text: "Tu contraseña se ha cambiado correctamente.",
                icon: "success",
            });
            setFormData({
                old_password: "",
                new_password: "",
                confirm_password: "",
            });
        } catch (error) {
            console.error("Error changing password:", error);
            const data = error.response?.data;
            if (error.response?.status === 400 && data && typeof data === "object") {
                // El backend devuelve errores por campo: { old_password: "...", new_password: [...] }
                const fieldErrors = {};
                for (const [field, msg] of Object.entries(data)) {
                    fieldErrors[field] = Array.isArray(msg) ? msg.join(" ") : String(msg);
                }
                setErrors(fieldErrors);
            } else {
                showErrorAlert({ text: "No se pudo cambiar la contraseña. Inténtalo de nuevo." });
            }
        } finally {
            setIsLoading(false);
        }
    };

    const getPasswordStrength = (password) => {
        if (!password) return { strength: 0, label: "", color: "" };

        let strength = 0;
        if (password.length >= 8) strength++;
        if (/[a-z]/.test(password)) strength++;
        if (/[A-Z]/.test(password)) strength++;
        if (/\d/.test(password)) strength++;
        if (/[^a-zA-Z\d]/.test(password)) strength++;

        if (strength <= 2) return { strength: strength * 20, label: "Débil", color: "progress-error" };
        if (strength <= 3) return { strength: strength * 20, label: "Media", color: "progress-warning" };
        return { strength: strength * 20, label: "Fuerte", color: "progress-success" };
    };

    const passwordStrength = getPasswordStrength(formData.new_password);

    const eyeToggle = (field) => (
        <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/50 hover:text-base-content transition-colors"
            onClick={() => togglePasswordVisibility(field)}
            aria-label={showPasswords[field] ? "Ocultar contraseña" : "Mostrar contraseña"}
        >
            {showPasswords[field] ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
    );

    return (
        <div className="glassCard rounded-2xl p-6 w-full">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-base-300">
                <div className="p-2 bg-primary/20 rounded-lg">
                    <ShieldCheck className="h-6 w-6 text-primary" />
                </div>
                <div>
                    <h2 className="text-xl font-bold text-base-content">Seguridad de la cuenta</h2>
                    <p className="text-base-content/70 text-sm">Cambia tu contraseña para mantener tu cuenta segura</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                {/* Contraseña actual */}
                <div className="form-control gap-1">
                    <label className="label py-0" htmlFor="old_password">
                        <span className="label-text text-sm font-semibold">Contraseña actual</span>
                    </label>
                    <TextInput
                        type={showPasswords.old_password ? "text" : "password"}
                        id="old_password"
                        name="old_password"
                        value={formData.old_password}
                        onChange={handleChange}
                        variant={errors.old_password ? "error" : undefined}
                        placeholder="Ingresa tu contraseña actual"
                        rightSlot={eyeToggle("old_password")}
                    />
                    {errors.old_password && (
                        <label className="label py-0">
                            <span className="label-text-alt text-error">{errors.old_password}</span>
                        </label>
                    )}
                </div>

                {/* Nueva contraseña */}
                <div className="form-control gap-1">
                    <label className="label py-0" htmlFor="new_password">
                        <span className="label-text text-sm font-semibold">Nueva contraseña</span>
                    </label>
                    <TextInput
                        type={showPasswords.new_password ? "text" : "password"}
                        id="new_password"
                        name="new_password"
                        value={formData.new_password}
                        onChange={handleChange}
                        variant={errors.new_password ? "error" : undefined}
                        placeholder="Ingresa tu nueva contraseña"
                        rightSlot={eyeToggle("new_password")}
                    />

                    {/* Indicador de fortaleza de contraseña */}
                    {formData.new_password && (
                        <div className="mt-2">
                            <div className="flex justify-between items-center mb-1">
                                <span className="text-xs text-base-content/70">Fortaleza de la contraseña</span>
                                <span
                                    className={`text-xs font-medium ${
                                        passwordStrength.color === "progress-error"
                                            ? "text-error"
                                            : passwordStrength.color === "progress-warning"
                                              ? "text-warning"
                                              : "text-success"
                                    }`}
                                >
                                    {passwordStrength.label}
                                </span>
                            </div>
                            <progress
                                className={`progress w-full ${passwordStrength.color}`}
                                value={passwordStrength.strength}
                                max="100"
                            ></progress>
                        </div>
                    )}

                    {errors.new_password && (
                        <label className="label py-0">
                            <span className="label-text-alt text-error">{errors.new_password}</span>
                        </label>
                    )}
                    <label className="label py-0">
                        <span className="label-text-alt text-base-content/50">
                            Debe tener al menos 8 caracteres, incluir mayúsculas, minúsculas y números
                        </span>
                    </label>
                </div>

                {/* Confirmar nueva contraseña */}
                <div className="form-control gap-1">
                    <label className="label py-0" htmlFor="confirm_password">
                        <span className="label-text text-sm font-semibold">Confirmar nueva contraseña</span>
                    </label>
                    <TextInput
                        type={showPasswords.confirm_password ? "text" : "password"}
                        id="confirm_password"
                        name="confirm_password"
                        value={formData.confirm_password}
                        onChange={handleChange}
                        variant={errors.confirm_password ? "error" : undefined}
                        placeholder="Confirma tu nueva contraseña"
                        rightSlot={eyeToggle("confirm_password")}
                    />
                    {errors.confirm_password && (
                        <label className="label py-0">
                            <span className="label-text-alt text-error">{errors.confirm_password}</span>
                        </label>
                    )}
                    {formData.confirm_password && formData.new_password === formData.confirm_password && (
                        <label className="label py-0">
                            <span className="label-text-alt text-success">✓ Las contraseñas coinciden</span>
                        </label>
                    )}
                </div>

                {/* Recomendaciones de seguridad */}
                <div role="alert" className="alert alert-info">
                    <KeyRound size={18} className="shrink-0" />
                    <div>
                        <h3 className="font-bold">Consejos de seguridad:</h3>
                        <div className="text-sm mt-1">
                            <ul className="list-disc list-inside space-y-1">
                                <li>Usa una contraseña única para esta cuenta</li>
                                <li>No compartas tu contraseña con nadie</li>
                                <li>Considera usar un gestor de contraseñas</li>
                                <li>Cambia tu contraseña regularmente</li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Acciones */}
                <div className="pt-4 border-t border-base-300 flex items-center justify-between gap-4">
                    <span className="text-sm text-base-content/70">Los cambios serán aplicados inmediatamente</span>
                    <button
                        type="submit"
                        disabled={isLoading}
                        className={`btn btn-primary btn-sm ${isLoading ? "loading" : ""}`}
                    >
                        {isLoading ? "Cambiando..." : "Cambiar contraseña"}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default SeguridadUserProfile;
